import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, CreditCard, Building, Clock, CheckCircle, 
  AlertCircle, Download, Plus, ExternalLink, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MerchantPayouts() {
  const [loading, setLoading] = useState(true);
  const [bankDetails, setBankDetails] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [balance, setBalance] = useState({
    available: 0,
    pending: 0,
    total_earned: 0,
    total_paid: 0
  });
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    swift_code: '',
    iban: '',
    country: ''
  });
  const [savingBank, setSavingBank] = useState(false);
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [merchant, setMerchant] = useState(null);

  // Get merchant data from localStorage
  useEffect(() => {
    const merchantData = localStorage.getItem('merchant_data');
    if (merchantData) {
      try {
        setMerchant(JSON.parse(merchantData));
      } catch (e) {
        console.error('Failed to parse merchant data:', e);
      }
    }
  }, []);

  const loadPayoutData = useCallback(async () => {
    if (!merchant?.id) return;
    
    setLoading(true);
    try {
      await Promise.all([
        loadBankDetails(),
        loadDashboardStats(),
        loadStripeStatus(),
        loadStripeBalance(),
        loadPayoutHistory()
      ]);
    } catch (error) {
      console.error('Failed to load payout data:', error);
    } finally {
      setLoading(false);
    }
  }, [merchant]);

  useEffect(() => {
    if (merchant?.id) {
      loadPayoutData();
    }
  }, [merchant, loadPayoutData]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('merchant_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadBankDetails = async () => {
    try {
      const bankRes = await fetch(`${API_URL}/api/merchant/bank-details`, {
        headers: getAuthHeaders()
      });
      const bankData = await bankRes.json();
      if (bankData.success && bankData.bank_details) {
        setBankDetails(bankData.bank_details);
      }
    } catch (error) {
      console.error('Failed to load bank details:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const dashRes = await fetch(`${API_URL}/api/merchant/dashboard`, {
        headers: getAuthHeaders()
      });
      const dashData = await dashRes.json();
      if (dashData.success) {
        const stats = dashData.dashboard.stats;
        setBalance({
          available: stats.total_commission_earned - (stats.total_commission_paid || 0),
          pending: stats.pending_commission || 0,
          total_earned: stats.total_commission_earned || 0,
          total_paid: stats.total_commission_paid || 0
        });
      }
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  const loadStripeStatus = async () => {
    if (!merchant?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/connect/accounts/${merchant.id}/status`);
      const data = await res.json();
      setStripeStatus(data);
    } catch (error) {
      console.error('Failed to load Stripe status:', error);
    }
  };

  const loadStripeBalance = async () => {
    if (!merchant?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/connect/accounts/${merchant.id}/balance`);
      const data = await res.json();
      if (data.success) {
        setStripeBalance(data);
      }
    } catch (error) {
      console.error('Failed to load Stripe balance:', error);
    }
  };

  const loadPayoutHistory = async () => {
    if (!merchant?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/connect/payouts/${merchant.id}?limit=10`);
      const data = await res.json();
      if (data.success && data.payouts) {
        setPayoutHistory(data.payouts);
      }
    } catch (error) {
      console.error('Failed to load payout history:', error);
      // Fallback to empty array
      setPayoutHistory([]);
    }
  };

  const connectStripeAccount = async () => {
    if (!merchant?.id) {
      toast.error('Merchant data not found. Please re-login.');
      return;
    }

    setConnectingStripe(true);
    try {
      const res = await fetch(`${API_URL}/api/connect/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: merchant.id,
          email: merchant.email,
          country: merchant.country || 'US',
          business_name: merchant.business_name || merchant.store_name || ''
        })
      });

      const data = await res.json();
      
      if (data.success && data.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = data.onboarding_url;
      } else if (data.mock) {
        toast.info('Stripe is in test mode. Real payouts require a live API key.');
        await loadStripeStatus();
      } else {
        toast.error(data.error || 'Failed to connect Stripe account');
      }
    } catch (error) {
      console.error('Failed to connect Stripe:', error);
      toast.error('Failed to connect Stripe account');
    } finally {
      setConnectingStripe(false);
    }
  };

  const refreshOnboardingLink = async () => {
    if (!merchant?.id) return;

    setConnectingStripe(true);
    try {
      const res = await fetch(`${API_URL}/api/connect/accounts/${merchant.id}/onboarding-link`, {
        method: 'POST'
      });

      const data = await res.json();
      
      if (data.success && data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error(data.error || 'Failed to get onboarding link');
      }
    } catch (error) {
      toast.error('Failed to get onboarding link');
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    
    if (!bankForm.account_holder || !bankForm.bank_name || !bankForm.account_number || !bankForm.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingBank(true);
    try {
      const res = await fetch(`${API_URL}/api/merchant/bank-details`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(bankForm)
      });

      if (res.ok) {
        toast.success('Bank details saved successfully');
        setShowBankForm(false);
        await loadBankDetails();
      } else {
        throw new Error('Failed to save bank details');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingBank(false);
    }
  };

  const requestPayout = async () => {
    // Check if Stripe is connected and enabled
    if (!stripeStatus?.has_stripe_account) {
      toast.error('Please connect your Stripe account first');
      return;
    }

    if (!stripeStatus?.payouts_enabled) {
      toast.error('Please complete Stripe onboarding to enable payouts');
      refreshOnboardingLink();
      return;
    }

    // Get available balance from Stripe
    const availableAmount = stripeBalance?.available?.[0]?.amount || 0;
    
    if (availableAmount < 5000) { // $50.00 in cents
      toast.error(`Minimum payout amount is $50. Your available balance is $${(availableAmount / 100).toFixed(2)}`);
      return;
    }

    setRequestingPayout(true);
    try {
      const res = await fetch(`${API_URL}/api/connect/payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: merchant.id,
          amount: availableAmount, // Payout full available balance
          currency: stripeBalance?.available?.[0]?.currency || 'usd',
          description: 'Commission payout request'
        })
      });

      const data = await res.json();
      
      if (data.success) {
        toast.success(`Payout of $${(availableAmount / 100).toFixed(2)} initiated! It will arrive in 2-3 business days.`);
        // Reload data
        await loadStripeBalance();
        await loadPayoutHistory();
      } else if (data.mock) {
        toast.info('Payout recorded (test mode). Real payouts require a live Stripe API key.');
        await loadPayoutHistory();
      } else {
        toast.error(data.error || 'Failed to create payout');
      }
    } catch (error) {
      console.error('Payout error:', error);
      toast.error('Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  // Helper to format amount from cents
  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  // Helper to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Calculate available balance from Stripe if connected, otherwise from dashboard stats
  const stripeAvailable = stripeBalance?.available?.[0]?.amount || 0;
  const stripePending = stripeBalance?.pending?.[0]?.amount || 0;
  const displayAvailable = stripeStatus?.has_stripe_account ? stripeAvailable / 100 : balance.available;
  const displayPending = stripeStatus?.has_stripe_account ? stripePending / 100 : balance.pending;

  return (
    <div className="space-y-6" data-testid="merchant-payouts">
      {/* Stripe Connect Status Banner */}
      {!stripeStatus?.has_stripe_account ? (
        <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg shrink-0">
              <CreditCard className="w-8 h-8 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Connect Stripe to Receive Payouts</h3>
              <p className="text-slate-300 text-sm mb-4">
                Set up your Stripe account to receive commission payments directly to your bank. 
                Stripe handles all payment processing securely.
              </p>
              <button
                onClick={connectStripeAccount}
                disabled={connectingStripe}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                data-testid="connect-stripe-btn"
              >
                {connectingStripe ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Connect with Stripe
              </button>
            </div>
          </div>
        </div>
      ) : !stripeStatus?.payouts_enabled ? (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-yellow-400 font-medium">Complete Stripe Onboarding</p>
            <p className="text-sm text-yellow-400/80 mt-1">
              Your Stripe account is connected but not fully verified. Complete the onboarding to enable payouts.
            </p>
            {stripeStatus?.currently_due?.length > 0 && (
              <p className="text-xs text-yellow-400/60 mt-2">
                Required: {stripeStatus.currently_due.join(', ')}
              </p>
            )}
            <button
              onClick={refreshOnboardingLink}
              disabled={connectingStripe}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg text-sm font-medium"
            >
              {connectingStripe ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Complete Verification
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div className="flex-1">
            <p className="text-green-400 font-medium">Stripe Connected</p>
            <p className="text-sm text-green-400/80">Your account is verified and ready to receive payouts.</p>
          </div>
          <button
            onClick={loadPayoutData}
            className="p-2 hover:bg-green-500/20 rounded-lg text-green-400"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Available Balance</p>
              <p className="text-3xl font-bold text-white mt-1">
                {stripeStatus?.has_stripe_account 
                  ? formatAmount(stripeAvailable, stripeBalance?.available?.[0]?.currency || 'usd')
                  : `$${displayAvailable.toFixed(2)}`
                }
              </p>
              {stripeStatus?.mock && (
                <p className="text-xs text-green-200 mt-1">(Test Mode)</p>
              )}
            </div>
            <DollarSign className="w-10 h-10 text-white/30" />
          </div>
          <button
            onClick={requestPayout}
            disabled={!stripeStatus?.payouts_enabled || stripeAvailable < 5000 || requestingPayout}
            className="mt-4 w-full py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="request-payout-btn"
          >
            {requestingPayout ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Request Payout'
            )}
          </button>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {stripeStatus?.has_stripe_account 
                  ? formatAmount(stripePending, stripeBalance?.pending?.[0]?.currency || 'usd')
                  : `$${displayPending.toFixed(2)}`
                }
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-400/50" />
          </div>
          <p className="text-xs text-slate-500 mt-2">Arriving in 2-7 days</p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Earned</p>
              <p className="text-2xl font-bold text-white mt-1">${balance.total_earned.toFixed(2)}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400/50" />
          </div>
          <p className="text-xs text-slate-500 mt-2">Lifetime earnings</p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Paid Out</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">${balance.total_paid.toFixed(2)}</p>
            </div>
            <CreditCard className="w-8 h-8 text-blue-400/50" />
          </div>
          <p className="text-xs text-slate-500 mt-2">All time payouts</p>
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Bank Account</h3>
          {!showBankForm && (
            <button
              onClick={() => setShowBankForm(true)}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
            >
              <Plus className="w-4 h-4" />
              {bankDetails ? 'Update' : 'Add'} Bank Details
            </button>
          )}
        </div>

        {showBankForm ? (
          <form onSubmit={handleSaveBankDetails} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Account Holder Name *</label>
                <input
                  type="text"
                  value={bankForm.account_holder}
                  onChange={(e) => setBankForm(f => ({ ...f, account_holder: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Bank Name *</label>
                <input
                  type="text"
                  value={bankForm.bank_name}
                  onChange={(e) => setBankForm(f => ({ ...f, bank_name: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="Bank of America"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Account Number *</label>
                <input
                  type="text"
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm(f => ({ ...f, account_number: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="****1234"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Routing Number</label>
                <input
                  type="text"
                  value={bankForm.routing_number}
                  onChange={(e) => setBankForm(f => ({ ...f, routing_number: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="021000021"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">SWIFT Code</label>
                <input
                  type="text"
                  value={bankForm.swift_code}
                  onChange={(e) => setBankForm(f => ({ ...f, swift_code: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="BOFAUS3N"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-2">Country *</label>
                <input
                  type="text"
                  value={bankForm.country}
                  onChange={(e) => setBankForm(f => ({ ...f, country: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  placeholder="United States"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowBankForm(false)}
                className="px-6 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingBank}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50"
              >
                {savingBank ? 'Saving...' : 'Save Bank Details'}
              </button>
            </div>
          </form>
        ) : bankDetails ? (
          <div className="flex items-center gap-4 p-4 bg-slate-900/50 rounded-lg">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Building className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-white">{bankDetails.bank_name}</p>
              <p className="text-sm text-slate-400">
                {bankDetails.account_holder} • {bankDetails.account_number}
              </p>
            </div>
            {bankDetails.verified ? (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" /> Verified
              </span>
            ) : (
              <span className="flex items-center gap-1 text-yellow-400 text-sm">
                <Clock className="w-4 h-4" /> Pending verification
              </span>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No bank account added yet</p>
            <button
              onClick={() => setShowBankForm(true)}
              className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
            >
              Add Bank Account
            </button>
          </div>
        )}
      </div>

      {/* Payout History */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Payout History</h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={loadPayoutHistory}
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>

        {payoutHistory.length === 0 ? (
          <div className="text-center py-8">
            <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No payouts yet</p>
            <p className="text-sm text-slate-500 mt-1">
              {stripeStatus?.payouts_enabled 
                ? 'Request a payout when your available balance reaches $50'
                : 'Connect Stripe and build up your balance to request payouts'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-slate-700">
                  <th className="pb-3 text-slate-400 font-medium text-sm">Date</th>
                  <th className="pb-3 text-slate-400 font-medium text-sm">Payout ID</th>
                  <th className="pb-3 text-slate-400 font-medium text-sm">Amount</th>
                  <th className="pb-3 text-slate-400 font-medium text-sm">Status</th>
                  <th className="pb-3 text-slate-400 font-medium text-sm">Arrival</th>
                </tr>
              </thead>
              <tbody>
                {payoutHistory.map((payout, idx) => (
                  <tr key={payout.payout_id || idx} className="border-b border-slate-700/50">
                    <td className="py-3 text-slate-300">
                      {formatDate(payout.created || payout.created_at)}
                    </td>
                    <td className="py-3 text-slate-400 text-sm font-mono">
                      {payout.payout_id?.substring(0, 16)}...
                    </td>
                    <td className="py-3 text-white font-medium">
                      {formatAmount(payout.amount, payout.currency || 'usd')}
                    </td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded capitalize ${
                        payout.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                        payout.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        payout.status === 'in_transit' ? 'bg-blue-500/20 text-blue-400' :
                        payout.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {payout.status === 'in_transit' ? 'In Transit' : payout.status}
                      </span>
                    </td>
                    <td className="py-3 text-slate-400 text-sm">
                      {payout.arrival_date ? formatDate(payout.arrival_date) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-blue-400 font-medium">Payout Information</p>
          <ul className="text-sm text-blue-400/80 mt-1 space-y-1">
            <li>• Minimum payout amount: $50</li>
            <li>• Payouts are processed via Stripe Express</li>
            <li>• Bank transfers take 2-3 business days</li>
            <li>• Commission rate: 10% of each sale</li>
            {stripeStatus?.mock && (
              <li className="text-yellow-400">• Currently in TEST MODE - connect real Stripe for live payouts</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
